import { useState } from "react";
import type { WidgetProps } from "../../lib/schema-form/types";
import { describeEnrichment, type EnrichmentDraft, type EnrichmentKind } from "./enrichment-copy";
import { EnrichmentEditor } from "./EnrichmentEditor";

/** A connection the picker can offer. `environment` mirrors the connector
 *  instance's env scope: `null`/undefined means any-env (always offered);
 *  a concrete value is offered only when it matches the pipeline's
 *  environment — filtering happens where this list is built (DEV-6562). */
export interface EnrichmentConnection { id: string; name: string; connectorType: string; environment?: string | null }
/** What the wizard hands the widget via widgetContext. */
export interface EnrichmentContext {
  connections: EnrichmentConnection[];
  discover?: (instanceId: string, kind: string) => Promise<{ items: { id: string; name: string }[] }>;
}

function asList(value: unknown): EnrichmentDraft[] {
  return Array.isArray(value) ? (value as EnrichmentDraft[]) : [];
}

const DEFAULT_KIND: EnrichmentKind = "profile-field";

export function EnrichmentsField({ value, onChange, errors, context }: WidgetProps) {
  const list = asList(value);
  const ctx = (context as EnrichmentContext) ?? { connections: [] };
  const [editing, setEditing] = useState<number | null>(null);

  function update(i: number, next: EnrichmentDraft) {
    onChange(list.map((e, idx) => (idx === i ? next : e)));
  }
  function add() {
    onChange([...list, { kind: DEFAULT_KIND } as EnrichmentDraft]);
    setEditing(list.length);
  }
  function remove(i: number) {
    onChange(list.filter((_, idx) => idx !== i));
    // `editing` is an array index; removing an earlier row shifts the rest down,
    // so decrement to keep the same editor open. Removing the open row closes it.
    setEditing((cur) => {
      if (cur === null || cur === i) return null;
      return cur > i ? cur - 1 : cur;
    });
  }

  return (
    <div className="enrich">
      <div className="enrich__head uppercase-label">Extra info added to each message</div>
      {list.length === 0 && <div className="enrich__empty">No extra info added yet.</div>}
      {list.map((e, i) => (
        <div className="enrich__row" key={i}>
          {editing === i ? (
            <div className="enrich__editor">
              <EnrichmentEditor
                draft={e}
                connections={ctx.connections}
                discover={ctx.discover}
                errors={errors}
                index={i}
                onChange={(next) => update(i, next)}
                onDone={() => setEditing(null)}
              />
            </div>
          ) : (
            <div className="enrich__summary">
              <span>{describeEnrichment(e)}</span>
              <span className="enrich__actions">
                <button type="button" className="btn btn--ghost" aria-label={`edit lookup ${i + 1}`} onClick={() => setEditing(i)}>EDIT</button>
                <button type="button" className="btn btn--ghost" aria-label={`remove lookup ${i + 1}`} onClick={() => remove(i)}>REMOVE</button>
              </span>
            </div>
          )}
        </div>
      ))}
      <button type="button" className="btn btn--secondary enrich__add" onClick={add}>+ Add a lookup</button>
    </div>
  );
}
